import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, Search, Compass, Sparkles, Menu as MenuIcon,
  User, UserCircle, Heart, Clock, Activity, Building2, ListChecks, Bell, MessageSquare,
  Users, HardHat, Calculator, TrendingUp, Scale, ShieldCheck, Truck,
  HelpCircle, Phone, Settings, Globe, Info, LogOut, Plus, Sun, Moon, LayoutDashboard, ChevronRight,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { openAbayAI } from "@/lib/abayAiBus";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const ACTIVE = "#1E40AF"; // primary blue for the active tab

const haptic = () => { try { if (navigator.vibrate) navigator.vibrate(10); } catch (e) { void e; } };

const TABS = [
  { key: "home", label: "Home", icon: Home, to: "/" },
  { key: "search", label: "Search", icon: Search, to: "/map-search" },
  { key: "explore", label: "Explore", icon: Compass, to: "/map-search?view=map" },
];

export const BottomNav = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { count: unreadMessages } = useUnreadMessages();
  const { t, i18n } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);

  const displayName = profile?.full_name || user?.email || "";

  const isExplore = pathname.startsWith("/map-search") && new URLSearchParams(search).get("view") === "map";
  const isSearch = pathname.startsWith("/map-search") && !isExplore;
  const activeKey = pathname === "/" ? "home" : isExplore ? "explore" : isSearch ? "search" : "";

  const go = (to, auth = false) => { haptic(); setMoreOpen(false); navigate(auth && !user ? "/login" : to); };
  const handleSignOut = async () => { setMoreOpen(false); await signOut(); toast.success("Signed out"); navigate("/"); };
  const setLang = (lng) => {
    i18n.changeLanguage(lng);
    setMoreOpen(false);
  };

  // ---- More drawer sections --------------------------------------------
  const accountItems = user
    ? [
        { label: "My Profile", icon: UserCircle, to: "/account/profile", testid: "more-profile" },
        { label: "My Listings", icon: ListChecks, to: "/dashboard?tab=listings", testid: "more-listings" },
        { label: "Saved Properties", icon: Heart, to: "/dashboard?tab=saved", testid: "more-saved" },
        { label: "Recently Viewed", icon: Clock, to: "/dashboard?tab=recent", testid: "more-recent" },
        { label: "My Activity", icon: Activity, to: "/dashboard?tab=activity", testid: "more-activity" },
        { label: "Notifications", icon: Bell, to: "/dashboard?tab=alerts", testid: "more-notifications" },
        { label: "Messages", icon: MessageSquare, to: "/messages", badge: unreadMessages, testid: "more-messages" },
        { label: "Dashboard", icon: LayoutDashboard, to: "/dashboard", testid: "more-dashboard" },
      ]
    : [];

  const servicesItems = [
    { label: "Agents", icon: Users, to: "/agents", testid: "more-agents" },
    { label: "Developers", icon: HardHat, to: "/developers", testid: "more-developers" },
    { label: "Mortgage Calculator", icon: Calculator, to: "/invest", testid: "more-mortgage" },
    { label: "Property Valuation", icon: TrendingUp, to: "/invest", testid: "more-valuation" },
    { label: "Legal Services", icon: Scale, to: "/help", testid: "more-legal" },
    { label: "Insurance", icon: ShieldCheck, to: "/help", testid: "more-insurance" },
    { label: "Moving Services", icon: Truck, to: "/help", testid: "more-moving" },
  ];

  const helpItems = [
    { label: "Help Center", icon: HelpCircle, to: "/help", testid: "more-help" },
    { label: "Contact Us", icon: Phone, to: "/contact", testid: "more-contact" },
    { label: "FAQ", icon: HelpCircle, to: "/faq", testid: "more-faq" },
    { label: "About ABAY360", icon: Info, to: "/about", testid: "more-about" },
  ];

  const Row = ({ item }) => (
    <button
      type="button"
      data-testid={item.testid}
      onClick={() => go(item.to, item.auth)}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted"
    >
      <item.icon className="h-5 w-5 text-muted-foreground" />
      <span className="flex-1 text-left">{item.label}</span>
      {item.badge > 0 && (
        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#10B981] px-1 text-[10px] font-semibold text-white">
          {item.badge > 9 ? "9+" : item.badge}
        </span>
      )}
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );

  const SectionTitle = ({ children }) => (
    <p className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</p>
  );

  const Tab = ({ tab }) => {
    const active = activeKey === tab.key;
    return (
      <Link
        to={tab.to}
        onClick={haptic}
        data-testid={`bottomnav-${tab.key}`}
        aria-current={active ? "page" : undefined}
        className="relative flex flex-1 flex-col items-center justify-center gap-1 py-2 outline-none"
      >
        {active && (
          <motion.span
            layoutId="bnav-indicator"
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            className="absolute top-0 h-1 w-8 rounded-full"
            style={{ backgroundColor: ACTIVE }}
          />
        )}
        <motion.span animate={{ scale: active ? 1.06 : 1 }} transition={{ duration: 0.2 }} style={{ color: active ? ACTIVE : undefined }} className={active ? "" : "text-muted-foreground"}>
          <tab.icon className="h-6 w-6" strokeWidth={active ? 2.4 : 2} />
        </motion.span>
        <span className="text-[11px] font-medium leading-none transition-colors duration-200" style={{ color: active ? ACTIVE : undefined }}>
          <span className={active ? "" : "text-muted-foreground"}>{t(`bottomnav.${tab.key}`, { defaultValue: tab.label })}</span>
        </span>
      </Link>
    );
  };

  return (
    <>
      <nav
        data-testid="mobile-bottom-nav"
        className="fixed inset-x-0 bottom-0 z-[200] border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden"
      >
        <ul className="mx-auto flex min-h-[64px] max-w-lg items-stretch justify-between px-1">
          {TABS.map((tb) => <li key={tb.key} className="flex-1"><Tab tab={tb} /></li>)}

          {/* Abay AI */}
          <li className="flex-1">
            <button
              type="button"
              data-testid="bottomnav-abay-ai"
              onClick={() => { haptic(); openAbayAI(); }}
              className="relative flex w-full flex-col items-center justify-center gap-1 py-2 text-muted-foreground outline-none"
            >
              <Sparkles className="h-6 w-6" />
              <span className="text-[11px] font-medium leading-none">{t("bottomnav.ai")}</span>
            </button>
          </li>

          {/* More */}
          <li className="flex-1">
            <button
              type="button"
              data-testid="bottomnav-more"
              onClick={() => { haptic(); setMoreOpen(true); }}
              className="relative flex w-full flex-col items-center justify-center gap-1 py-2 outline-none"
            >
              {moreOpen && (
                <motion.span layoutId="bnav-indicator" transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  className="absolute top-0 h-1 w-8 rounded-full" style={{ backgroundColor: ACTIVE }} />
              )}
              <MenuIcon className="h-6 w-6" style={{ color: moreOpen ? ACTIVE : undefined }} />
              <span className="text-[11px] font-medium leading-none" style={{ color: moreOpen ? ACTIVE : undefined }}>
                <span className={moreOpen ? "" : "text-muted-foreground"}>{t("bottomnav.more")}</span>
              </span>
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="right" className="flex w-[88%] max-w-sm flex-col p-0" data-testid="more-drawer">
          <SheetHeader className="border-b border-border px-4 py-3.5">
            <SheetTitle className="text-left text-base font-bold text-foreground">More</SheetTitle>
            <SheetDescription className="sr-only">Account, services, help and preferences menu</SheetDescription>
          </SheetHeader>

          <div className="thin-scroll flex-1 overflow-y-auto px-3 pb-4">
            {/* Account header */}
            {user ? (
              <div className="mt-3 flex items-center gap-3 rounded-xl bg-muted px-3 py-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E40AF] text-sm font-semibold text-white">
                  {(displayName || "A").trim().charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-foreground">{displayName}</span>
                  <span className="block text-xs capitalize text-muted-foreground">{profile?.role || "buyer"}</span>
                </span>
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button data-testid="more-login" variant="outline" onClick={() => go("/login")} className="rounded-xl">Login</Button>
                <Button data-testid="more-signup" onClick={() => go("/signup")} className="rounded-xl bg-[#1E40AF] text-white hover:bg-[#17348f]">Register</Button>
              </div>
            )}

            {user && (<><SectionTitle>My Account</SectionTitle>{accountItems.map((i) => <Row key={i.testid} item={i} />)}</>)}

            <SectionTitle>Services</SectionTitle>
            {servicesItems.map((i) => <Row key={i.testid} item={i} />)}

            <SectionTitle>Help &amp; Support</SectionTitle>
            {helpItems.map((i) => <Row key={i.testid} item={i} />)}

            <SectionTitle>Preferences</SectionTitle>
            <button type="button" data-testid="more-theme-toggle" onClick={() => { toggleTheme(); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted">
              {theme === "dark" ? <Sun className="h-5 w-5 text-muted-foreground" /> : <Moon className="h-5 w-5 text-muted-foreground" />}
              <span className="flex-1 text-left">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </button>
            <div className="flex flex-col gap-2 rounded-xl px-3 py-2.5" data-testid="more-language">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 text-[15px] font-medium text-foreground">{t("common.changeLanguage")}</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {SUPPORTED_LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`more-lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`rounded-lg border py-1.5 text-xs font-semibold transition-colors duration-200 ${
                      i18n.language === l.code
                        ? "border-[#1E40AF] bg-[#1E40AF]/10 text-[#1E40AF]"
                        : "border-border text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" data-testid="more-settings" onClick={() => go("/account/profile", true)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground transition-colors duration-200 hover:bg-muted">
              <Settings className="h-5 w-5 text-muted-foreground" /><span className="flex-1 text-left">Settings</span><ChevronRight className="h-4 w-4 text-slate-300" />
            </button>

            {user && (
              <button type="button" data-testid="more-logout" onClick={handleSignOut}
                className="mt-2 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-[#DC2626] transition-colors duration-200 hover:bg-[#DC2626]/10">
                <LogOut className="h-5 w-5" /> Log out
              </button>
            )}
          </div>

          <div className="border-t border-border p-3">
            <SheetClose asChild>
              <Button onClick={() => go("/list-property", true)} data-testid="more-list-property" className="w-full rounded-xl bg-[#10B981] text-white hover:bg-[#0E9F6E]">
                <Plus className="mr-1 h-4 w-4" /> List Property
              </Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default BottomNav;
